import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../../services';
import { usePostHog } from '@posthog/react';

const RubricLogo = ({ white = false }) => {
    const color = white ? '#ffffff' : '#000000';
    return (
        <svg viewBox="30 45 465 110" height="34" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(45, 65) scale(0.68)">
                <rect x="0" y="0" width="36" height="36" rx="4" fill={color} />
                <rect x="48" y="0" width="36" height="36" rx="4" fill="none" stroke={color} strokeWidth="6" />
                <rect x="0" y="48" width="36" height="36" rx="4" fill={color} />
                <rect x="48" y="48" width="36" height="36" rx="4" fill={color} />
            </g>
            <text
                x="118" y="122"
                fill={color}
                style={{ fontFamily: "'Source Code Pro', monospace", fontSize: '72px', fontWeight: '700' }}
            >
                rubricHQ
            </text>
        </svg>
    );
};

const Register = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const posthog = usePostHog();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== passwordConfirmation) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await authAPI.register({
                name,
                email,
                password,
                password_confirmation: passwordConfirmation,
                company_name: companyName,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });

            if (response?.status?.code === 200 || response?.status?.code === 201) {
                posthog?.capture('user_signed_up', { method: 'email' });
                setSuccess(response?.status?.message || 'Account created successfully! Redirecting to login...');
                setTimeout(() => navigate('/login'), 2500);
            } else {
                setError(response?.status?.message || 'Registration failed. Please try again.');
            }
        } catch (err) {
            const msg = err.response?.data?.status?.message || err.response?.data?.message || 'Registration failed. Please try again.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = () => {
        const returnUrl = encodeURIComponent(`${window.location.origin}/`);
        window.location.href = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/users/auth/google_oauth2?return_to=${returnUrl}`;
    };

    return (
        <div className="min-h-screen flex">
            {/* Left branding panel */}
            <div className="hidden lg:flex lg:w-5/12 bg-black flex-col justify-between p-12">
                <RubricLogo white />
                <div>
                    <h2 className="text-white text-4xl font-light leading-snug mb-4">
                        Start testing smarter,<br />
                        ship better AI.
                    </h2>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                        Join teams using rubricHQ to catch AI failures before your customers do.
                    </p>
                </div>
                <p className="text-muted-foreground text-xs">© 2026 RubricMind Inc. All rights reserved.</p>
            </div>

            {/* Right form panel */}
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 bg-card">
                {/* Mobile logo */}
                <div className="lg:hidden mb-10">
                    <RubricLogo />
                </div>

                <div className="w-full max-w-md">
                    <div className="mb-8">
                        <h1 className="text-2xl font-semibold text-foreground mb-1">Create your account</h1>
                        <p className="text-muted-foreground text-sm">Get started with rubricHQ for free</p>
                    </div>

                    {/* Google button */}
                    <button
                        onClick={handleGoogleSignup}
                        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                    >
                        <img
                            className="h-5 w-5"
                            src="https://www.svgrepo.com/show/475656/google-color.svg"
                            alt="Google"
                        />
                        Continue with Google
                    </button>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center">
                            <span className="bg-card px-3 text-xs text-muted-foreground">or continue with email</span>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {success && (
                            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                                {success}
                            </div>
                        )}
                        {error && (
                            <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">
                                    Full name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                                    placeholder="Jane Smith"
                                />
                            </div>
                            <div>
                                <label htmlFor="companyName" className="block text-sm font-medium text-foreground mb-1.5">
                                    Company
                                </label>
                                <input
                                    id="companyName"
                                    name="companyName"
                                    type="text"
                                    required
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                                    placeholder="Acme Inc."
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                                placeholder="you@company.com"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                                    placeholder="Min. 8 characters"
                                />
                            </div>
                            <div>
                                <label htmlFor="passwordConfirmation" className="block text-sm font-medium text-foreground mb-1.5">
                                    Confirm password
                                </label>
                                <input
                                    id="passwordConfirmation"
                                    name="passwordConfirmation"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={passwordConfirmation}
                                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                                    placeholder="Repeat password"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || success}
                            className="w-full py-2.5 px-4 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Creating account...' : 'Create account'}
                        </button>
                    </form>

                    <p className="mt-4 text-center text-xs text-muted-foreground leading-relaxed">
                        By creating an account, you agree to our{' '}
                        <a
                            href="https://rubrichq.io/privacy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-foreground hover:underline"
                        >
                            Privacy Policy
                        </a>{' '}
                        &amp;{' '}
                        <a
                            href="https://rubrichq.io/terms/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-foreground hover:underline"
                        >
                            Terms
                        </a>
                        .
                    </p>

                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <Link to="/login" className="font-medium text-foreground hover:underline">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;
